import { Component, Input, ViewChild, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

import { BarreOngletsComponent, DonneesColonne, DonneesColonneBoutonIconeClick, DonneesMenuItem, DonneesOnglet, FonteIconeComponent, GrilleComponent, SimpleLibelleComponent, SimplePanelComponent, TelechargementFichier, AttenteOperationComponent, DonneesGrilleSelection } from '../../../../../../../val-ammon-composants/val-composants-lib/src/public-api';
import { RetournerVueAchatResultat } from '../../../RetournerVueAchatResultat';
import { EFactAchatService } from '../../../val-efact-achat.service';
import { TranslocoCurrencyPipe, TranslocoDatePipe } from '@jsverse/transloco-locale';
import { RetournerVueAchatDocumentResultat, RetournerVueAchatHistoriqueStatutResultat } from '../../../../public-api';
import { GererHabilitationsMenuItems, PermissionsService } from '../../../../../../../val-ammon-permissions/val-permissions-lib/src/public-api';
import { PDFViewerModule } from '@progress/kendo-angular-pdfviewer';

// Composant contenu réutilisable pour la fiche de facture d'achat
// Utilisé à la fois dans le modal (fiche générique) et dans la page dédiée
@Component(
  {
    selector: 'val-facture-achat-fiche-contenu',
    imports: [
      SimplePanelComponent,
      SimpleLibelleComponent,
      FonteIconeComponent,
      TranslocoDatePipe,
      TranslocoCurrencyPipe,
      GrilleComponent,
      BarreOngletsComponent,
      AttenteOperationComponent,
      PDFViewerModule,
      TranslocoPipe
    ],
    templateUrl: './vue-facture-achat-fiche-contenu.component.html',
    styleUrls: ['./vue-facture-achat-fiche-contenu.component.scss']
  })
export class VueFactureAchatFicheContenuComponent implements OnInit {
  
  // ID de l'achat
  private achatId!: string;

  // Barre d'onglets
  @ViewChild("barreOnglets")
  public barreOnglets!: BarreOngletsComponent;

  // Grille de l'historique : n'existe que tant que l'onglet Historique est affiché
  @ViewChild("grilleHistorique")
  public grilleHistorique!: GrilleComponent;

  // Onglets
  public onglets: DonneesOnglet[];

  // Données de consultation
  public donnees: RetournerVueAchatResultat | null;

  // Colonnes documents liés à l'achat
  public colonnes_documents: DonneesColonne[];

  // Données documents liés à l'achat
  public donnees_documents: RetournerVueAchatDocumentResultat[];

  // Colonnes de l'historique des statuts de la facture
  public colonnes_historique: DonneesColonne[];

  // Données de l'historique des statuts de la facture, rendues par l'API du plus récent au plus ancien
  public donnees_historique: RetournerVueAchatHistoriqueStatutResultat[];

  // Indique si une opération est en cours
  public operationEnCours: boolean;

  public menuItems: DonneesMenuItem[];

  private ongletDocuments: DonneesOnglet;

  private ongletHistorique: DonneesOnglet;

  private ongletDetails: DonneesOnglet;

  // Nombre de documents affiché dans le libellé de l'onglet Documents (0 : pas de compteur)
  private nombreDocuments: number;

  // Nombre de lignes d'historique affiché dans le libellé de l'onglet Historique (0 : pas de compteur)
  private nombreHistorique: number;

  // Document sélectionné dans la grille
  public documentSelectionne: RetournerVueAchatDocumentResultat | null;

  // URL du document sélectionné pour le viewer PDF
  public urlDocumentSelectionne: string | undefined;

  // Nom du fichier du document sélectionné pour le viewer PDF
  public nomFichierDocumentSelectionne: string;

  // Indique si le panel « Retour de l'acheteur » est affiché
  public afficherRetourAcheteur: boolean;

  // Ligne d'historique sélectionnée portant un motif ou une note
  public retourAcheteurSelectionne: RetournerVueAchatHistoriqueStatutResultat | null;

  // Création
  constructor(private eFactAchatService: EFactAchatService, private permissionsService: PermissionsService,
              private translocoService: TranslocoService) {
    this.donnees = new RetournerVueAchatResultat();
    this.colonnes_documents = [];
    this.donnees_documents = [];
    this.colonnes_historique = [];
    this.donnees_historique = [];
    this.menuItems = [];
    this.operationEnCours = false;
    this.documentSelectionne = null;
    this.urlDocumentSelectionne = undefined;
    this.nomFichierDocumentSelectionne = "";
    this.afficherRetourAcheteur = false;
    this.retourAcheteurSelectionne = null;
    this.nombreDocuments = 0;
    this.nombreHistorique = 0;

    // Les libellés des onglets sont posés par MettreAJourLibellesOnglets, dans la langue active
    this.ongletDetails = new DonneesOnglet();
    this.ongletDetails.identifiant = 'details';

    this.ongletDocuments = new DonneesOnglet();
    this.ongletDocuments.identifiant = 'documents';

    this.ongletHistorique = new DonneesOnglet();
    this.ongletHistorique.identifiant = 'historique';

    this.onglets = [this.ongletDetails, this.ongletDocuments, this.ongletHistorique];

    // Libellés des onglets et des colonnes des grilles : construits une fois les traductions
    // chargées, puis reconstruits à chaque changement de langue, sans rechargement de la page.
    this.translocoService.selectTranslation()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.MettreAJourLibellesOnglets();
        this.CreerColonnesDocuments();
        this.CreerColonnesHistorique();
      });
  }

  // Initialise le composant
  public async ngOnInit(): Promise<void> {
    this.CreerMenuItems();
  }

  // Met à jour les libellés des onglets dans la langue active, compteurs compris
  private MettreAJourLibellesOnglets(): void {
    this.ongletDetails.libelle = this.translocoService.translate('achat.fiche.onglets.details');

    this.ongletDocuments.libelle = this.nombreDocuments > 0
      ? this.translocoService.translate('achat.fiche.onglets.documents-nombre', { nombre: this.nombreDocuments })
      : this.translocoService.translate('achat.fiche.onglets.documents');

    this.ongletHistorique.libelle = this.nombreHistorique > 0
      ? this.translocoService.translate('achat.fiche.onglets.historique-nombre', { nombre: this.nombreHistorique })
      : this.translocoService.translate('achat.fiche.onglets.historique');
  }

  // Indique si aucune donnée n'est disponible
  public AucuneDonnee(): boolean {
    return !this.donnees;
  }

  // Charge les données de l'achat
  public async ChargerDonnees(id: string): Promise<void> {
    this.achatId = id;
    this.ResetDonnees();
    this.ResetOnglets();
    this.ResetViewerPDF();

    await this.RecupererDonnees();
    this.RecupererDocuments();  // chargement en arrière plan
    this.RecupererHistorique(); // chargement en arrière plan
  }

  private ResetDonnees(): void {
    this.donnees = new RetournerVueAchatResultat();
    this.donnees_documents = [];
    this.donnees_historique = [];
    this.MasquerRetourAcheteur();
  }

  private ResetOnglets(): void {
    this.barreOnglets?.SelectionnerOnglet(this.onglets[0]);
    this.nombreDocuments = 0;
    this.nombreHistorique = 0;
    this.MettreAJourLibellesOnglets();
  }

  async RecupererDonnees() {
    try {
      let valApiResultat = await this.eFactAchatService.RetournerVueAchat(this.achatId);
      this.donnees = valApiResultat.resultat;
    } catch {
    }
  }

  // Crée le menu
  private async CreerMenuItems(): Promise<void> {
    let menuItems: DonneesMenuItem[];
    let gererHabilitationsMenuItems: GererHabilitationsMenuItems;
    const menuOuvrir: DonneesMenuItem = {dico: "D23F9BEE-04EF-47C1-B8F6-256863035361", iconeClass: "fas fa-file-archive", action: this.TelechargerTous.bind (this) };
    
    menuItems = [menuOuvrir];
    gererHabilitationsMenuItems = new GererHabilitationsMenuItems(menuItems, this.permissionsService);
    this.menuItems = await gererHabilitationsMenuItems.Executer();
  }    
  
  // Télécharge tous les documents liés à l'achat dans une archive zip
  private TelechargerTous(): void {
    this.eFactAchatService.RetournerDocumentsAchatZip(this.achatId).then(resultat => {
      if (resultat.status === "success" && resultat.resultat)
        TelechargementFichier.Telecharger(resultat.resultat.nomFichier, resultat.resultat.contenu, resultat.resultat.typeContenu);
    });
  }
  
  async RecupererDocuments() {
    try {
      let valApiResultat = await this.eFactAchatService.RetournerVueAchatDocuments(this.achatId);
      if (valApiResultat.status === "success" && valApiResultat.resultat)
        this.donnees_documents = valApiResultat.resultat;

      this.nombreDocuments = this.donnees_documents.length;
      this.MettreAJourLibellesOnglets();
    } catch {
    }
  }

  // Récupère l'historique des synchronisations de statut de la facture.
  // La liste est déjà ordonnée par date décroissante par l'API : ne pas la retrier ici.
  async RecupererHistorique() {
    // La ligne sélectionnée pointe sur l'ancien jeu de données : la sélection est perdue au rechargement.
    this.MasquerRetourAcheteur();

    try {
      let valApiResultat = await this.eFactAchatService.RetournerVueAchatHistoriqueStatuts(this.achatId);
      if (valApiResultat.status === "success" && valApiResultat.resultat)
        this.donnees_historique = valApiResultat.resultat;

      this.nombreHistorique = this.donnees_historique.length;
      this.MettreAJourLibellesOnglets();

      this.SelectionnerPremiereLigneHistorique();
    } catch {
    }
  }

  // Sélectionne la ligne la plus récente, ce qui ouvre le panel « Retour de l'acheteur »
  // si elle porte un motif ou une note. Même geste que sur la fiche de vente.
  private SelectionnerPremiereLigneHistorique(): void {
    if (!this.donnees_historique.length)
      return;

    // Attend le prochain cycle pour garantir la disponibilité de la grille et des données Kendo.
    // Onglet Historique masqué : la grille n'existe pas, la sélection se fera à son affichage.
    setTimeout(() => {
      const premierHistorique = this.donnees_historique[0];

      if (!premierHistorique || !this.grilleHistorique)
        return;

      this.grilleHistorique.donneesSelection.identifiantFocus = premierHistorique.id;
      this.grilleHistorique.SelectionChange();
    });
  }

  private CreerColonnesHistorique(): void {
    this.colonnes_historique =
    [
      {identifiant: "1", champ: "dateStatut", libelle: this.translocoService.translate("achat.commun.date"), format: "DAT_H", taille: 90, visible: true},
      {identifiant: "2", champ: "statut", libelle: this.translocoService.translate("achat.fiche.historique.statut"), format: "TXT", taille: 110, visible: true},
      {identifiant: "3", champ: "codeBrutPa", libelle: this.translocoService.translate("achat.fiche.historique.code-plateforme"), format: "TXT", taille: 130, visible: true},
      {identifiant: "4", champ: "source", libelle: this.translocoService.translate("achat.fiche.historique.source"), format: "TXT", taille: 110, visible: true},
      {identifiant: "5", champ: "issue", libelle: this.translocoService.translate("achat.fiche.historique.issue"), format: "TXT", taille: 110, visible: true},
      {identifiant: "6", champ: "existeMotifOuNote", libelle: this.translocoService.translate("achat.commun.motif"), format: "CHK", taille: 70, visible: true},
      {identifiant: "7", champ: "message", libelle: this.translocoService.translate("achat.fiche.historique.message"), format: "TXT", taille: 200, visible: true},
    ];
  }

  // Sélection d'une ligne d'historique : ouvre le panel « Retour de l'acheteur » quand la ligne
  // porte un motif ou une note, le referme sinon. Même geste que sur la fiche de vente.
  public HistoriqueSelectionChange(selection: DonneesGrilleSelection): void {
    if (!selection || !selection.identifiantFocus) {
      this.MasquerRetourAcheteur();
      return;
    }

    const historique = this.donnees_historique.find(ligne => ligne.id === selection.identifiantFocus);

    if (historique && historique.existeMotifOuNote) {
      this.retourAcheteurSelectionne = historique;
      this.afficherRetourAcheteur = true;
    }
    else
      this.MasquerRetourAcheteur();
  }

  private MasquerRetourAcheteur(): void {
    this.retourAcheteurSelectionne = null;
    this.afficherRetourAcheteur = false;
  }

  // Une note peut arriver sous forme de tableau JSON quand l'acheteur en a saisi plusieurs :
  // on l'affiche alors ligne à ligne, sinon telle quelle.
  public ParseNote(note: string): string[] {
    try {
      const parsed = JSON.parse(note);
      if (Array.isArray(parsed))
        return parsed;
    } catch {
      // Note simple : rien à découper
    }
    return [note];
  }

  private CreerColonnesDocuments(): void {
    this.colonnes_documents =
    [
      {identifiant: "1", champ: "libelle", libelle: this.translocoService.translate("achat.fiche.documents.libelle"), format: "TXT", taille: 150, visible: true},
      {identifiant: "2", champ: "typeDocument", libelle: this.translocoService.translate("achat.fiche.documents.type-document"), format: "TXT", taille: 100, visible: true},
      {identifiant: "3", champ: "dateCreation", libelle: this.translocoService.translate("achat.fiche.documents.date-creation"), format: "DAT", taille: 70, visible: true},
      {identifiant: "4", champ: "iconTelecharger", libelle: this.translocoService.translate("achat.fiche.documents.telecharger"), format: "BTN_ICON", taille: 50, visible: true},
    ];
  }
  
  public DocumentSelectionChange(selection: DonneesGrilleSelection): void {
    if (selection && selection.identifiantFocus) {
      const documentId = selection.identifiantFocus;
      const document = this.donnees_documents.find(doc => doc.id === documentId) || null;
      
      if (document) {
        // Charger le document pour l'afficher dans le viewer
        this.eFactAchatService.RetournerDocumentAchat(this.achatId, documentId).then(resultat => {
          if (resultat.status === "success" && resultat.resultat && resultat.resultat.typeContenu === "application/pdf") 
          {
            const blob = TelechargementFichier.CreerBlob(resultat.resultat.contenu, resultat.resultat.typeContenu);
              this.documentSelectionne = document;
            this.urlDocumentSelectionne = URL.createObjectURL(blob);
            this.nomFichierDocumentSelectionne = resultat.resultat.nomFichier;
          }
          else
          {
            this.ResetViewerPDF();
          }
        });
      }
    } else {
      this.ResetViewerPDF();
    }
  }
  
  public DocumentColonneBoutonIconeClick(evenement: DonneesColonneBoutonIconeClick): void {
    const documentId = evenement.identifiant;
    
    this.eFactAchatService.RetournerDocumentAchat(this.achatId, documentId).then(resultat => {
      if (resultat.status === "success" && resultat.resultat)
        TelechargementFichier.Telecharger(resultat.resultat.nomFichier, resultat.resultat.contenu, resultat.resultat.typeContenu);
    });
  }

  // Réinitialise le viewer PDF
  private ResetViewerPDF(): void {
    this.documentSelectionne = null;
    this.urlDocumentSelectionne = undefined;
    this.nomFichierDocumentSelectionne = '';
  }

  // Changement d'onglet
  public OngletSelectionneChange(onglet: DonneesOnglet): void {
    this.ResetViewerPDF();

    // La grille de l'historique est recréée à chaque affichage de l'onglet : resélectionner.
    if (onglet.identifiant === 'historique')
      this.SelectionnerPremiereLigneHistorique();
  }

  // Rafraîchissement de l'onglet
  public async RafraichissementOngletDemande(onglet: DonneesOnglet): Promise<void> {
    this.operationEnCours = true;

    if (onglet.identifiant === 'details') {
      await this.RecupererDonnees();
    }
    else if (onglet.identifiant === 'documents') {
      await this.RecupererDocuments();
      this.ResetViewerPDF();
    }
    else if (onglet.identifiant === 'historique') {
      await this.RecupererHistorique();
    }

    this.operationEnCours = false;
  }
}
